import { FC, ReactNode } from 'react';
import styled from 'styled-components';

interface BadgeProps {
  // Основные пропсы
  value?: string | number;
  show?: boolean;
  max?: number;
  type?: 'default' | 'success' | 'warning' | 'error' | 'info';
  color?: string;
  processing?: boolean;
  dot?: boolean;

  // Позиционирование
  offset?: [number, number];

  // Дочерние элементы
  children?: ReactNode;

  // Дополнительные стили
  className?: string;
}

const BadgeContainer = styled.div<{ $offset?: [number, number] }>`
  position: relative;
  display: inline-block;
  
  .badge {
    position: absolute;
    top: ${(props) => (props.$offset ? props.$offset[1] : 0)}px;
    right: ${(props) => (props.$offset ? props.$offset[0] : 0)}px;
    transform: translate(50%, -50%);
    z-index: 1;
  }
`;

const BadgeContent = styled.div<{
  $type?: BadgeProps['type'];
  $color?: string;
  $dot?: boolean;
  $processing?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${(props) => (props.$dot ? '8px' : '18px')};
  height: ${(props) => (props.$dot ? '8px' : '18px')};
  padding: ${(props) => (props.$dot ? '0' : '0 6px')};
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  border-radius: 9px;
  background-color: ${(props) => {
    if (props.$color) {
      return props.$color;
    }
    switch (props.$type) {
      case 'success':
        return '#18a058';
      case 'warning':
        return '#f0a020';
      case 'error':
        return '#d03050';
      case 'info':
        return '#2080f0';
      default:
        return '#18a058';
    }
  }};
  color: #fff;
  box-shadow: 0 0 0 1px #fff;
  
  ${(props) =>
    props.$processing &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 9px;
      border: 1px solid ${props.$color || '#18a058'};
      animation: badge-processing 1.2s infinite ease-in-out;
    }
  `}

  @keyframes badge-processing {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }
`;

export const UIBadge: FC<BadgeProps> = ({
  value,
  show = true,
  max,
  type = 'default',
  color,
  processing = false,
  dot = false,
  offset,
  children,
  className,
}) => {
  const renderContent = () => {
    if (!show) {
      return null;
    }
    if (dot) {
      return (
        <BadgeContent
          $dot
          $type={type}
          $color={color}
          $processing={processing}
        />
      );
    }

    let displayValue = value;
    if (typeof value === 'number' && max && value > max) {
      displayValue = `${max}+`;
    }

    return (
      <BadgeContent
        $type={type}
        $color={color}
        $processing={processing}
      >
        {displayValue}
      </BadgeContent>
    );
  };

  return (
    <BadgeContainer
      $offset={offset}
      className={className}
    >
      {children}
      {renderContent()}
    </BadgeContainer>
  );
};
